import React from 'react';
import { motion } from 'framer-motion';

const CorporateBrief = () => {
  const operatingSegments = [
    {
      title: 'Digital & Blockchain Infrastructure',
      description: 'Proprietary Layer-1 blockchain and financial infrastructure platforms.'
    },
    {
      title: 'Banking & Financial Services',
      description: 'Digital payments, financial rails, asset services, and regulated fintech infrastructure.'
    },
    {
      title: 'Artificial Intelligence & Enterprise Software',
      description: 'AI-driven enterprise automation and operational intelligence systems.'
    },
    {
      title: 'Consumer Electronics & Smart Devices',
      description: 'Design, development, and distribution of consumer and connected electronic products.'
    },
    {
      title: 'Construction & Real Assets',
      description: 'Residential and commercial development with a long-term ownership focus.'
    },
    {
      title: 'Logistics & Supply Chain',
      description: 'Integrated warehousing, fulfillment, and distribution operations.'
    },
    {
      title: 'Hospitality & Travel',
      description: 'Asset-backed hospitality and travel service platforms.'
    },
    {
      title: 'Fast-Moving Consumer Goods',
      description: 'Branded consumer products across food and beverage categories.'
    },
    {
      title: 'Fashion & Lifestyle',
      description: 'Consumer brands spanning apparel, lifestyle products, and brand-led experiences.'
    },
    {
      title: 'Education & Knowledge Platforms',
      description: 'Education initiatives, digital learning platforms, and skills development programs.'
    },
    {
      title: 'Social Impact & Foundations',
      description: 'Long-term humanitarian and community development initiatives, including Kibouor Foundation and Tau Foundation.'
    }
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Hero Section */}
      <section className="pt-32 pb-20 border-b border-border">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-left"
          >
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-4 leading-tight tracking-tight">
              AR Holdings Group Corp
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground mb-4 font-light italic">
              Tomorrow's Businesses, Today.
            </p>
            <p className="text-2xl md:text-3xl text-muted-foreground mb-2 font-light">
              A Diversified Global Operating and Investment Group
            </p>
            <p className="text-sm text-muted-foreground uppercase tracking-wider mt-6">
              Corporate Profile
            </p>
          </motion.div>
        </div>
      </section>

      {/* Corporate Brief (Above the Fold) */}
      <section className="py-20 border-b border-border">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-6 text-lg md:text-xl text-muted-foreground leading-relaxed"
          >
            <p>
              AR Holdings Group Corp is a privately held, diversified global operating and investment group with interests across digital infrastructure, enterprise technology, real assets, consumer brands, logistics, hospitality, and social impact initiatives.
            </p>
            <p>
              The Group is structured to build, own, and operate foundational platforms supporting both digital and physical economies. Its portfolio combines advanced technology infrastructure with asset-backed businesses to enable long-term value creation, operational resilience, and capital discipline.
            </p>
            <p>
              AR Holdings engages selectively with institutions, partners, and governments aligned with its long-term orientation, governance standards, and infrastructure-led approach to growth.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mandate */}
      <section className="py-20 border-b border-border">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-8 tracking-tight">Mandate</h2>
            <ul className="space-y-4 text-lg md:text-xl text-muted-foreground leading-relaxed list-none">
              <li className="pl-0">• Build and operate scalable platforms across critical industries</li>
              <li className="pl-0">• Deploy blockchain and artificial intelligence as foundational infrastructure</li>
              <li className="pl-0">• Integrate digital systems with physical assets within a unified group structure</li>
              <li className="pl-0">• Maintain disciplined governance and long-term capital stewardship</li>
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Operating Segments */}
      <section className="py-20 border-b border-border">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-12 tracking-tight">Operating Segments</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {operatingSegments.map((segment, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="border-b border-border pb-6"
                >
                  <h3 className="text-xl font-semibold mb-3 tracking-tight">{segment.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{segment.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Ecosystem Model */}
      <section className="py-20 border-b border-border">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-8 tracking-tight">Ecosystem Model</h2>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
              AR Holdings Group Corp is structured as an integrated ecosystem rather than a collection of standalone businesses. Digital infrastructure, physical assets, and consumer-facing operations reinforce one another to enhance efficiency, resilience, and long-term value creation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Governance */}
      <section className="py-20 border-b border-border">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-8 tracking-tight">Governance</h2>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
              The Group operates under a centralized governance framework with decentralized execution across business units. Emphasis is placed on accountability, risk management, compliance discipline, and long-term leadership continuity.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Engagement Approach */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-8 tracking-tight">Engagement Approach</h2>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
              AR Holdings engages in partnerships selectively, prioritizing long-term alignment, institutional discipline, and strategic relevance. Relationships are approached as enduring collaborations rather than transactional engagements.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CorporateBrief;

